import { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { createPath } from 'history';
import useURLSearchParams from './useURLSearchParams';

const useMemoizedSort = (data, key, ascending, handler) =>
    useMemo(
        () => handler({ data, key, ascending }),
        [data, key, ascending, handler],
    );

const useMaxSortMetric = (sortedData, sortOption) =>
    useMemo(() => {
        let max = 0;
        if (!sortedData) return max;

        const growth = ['YoY', 'MoM'].includes(sortOption.sortBy);

        for (let i = 0; i < sortedData.length; i++) {
            const n = Math.abs(sortedData[i][sortOption.sortBy]);
            if (Number.isNaN(n)) continue;
            max = Math.max(max, n);
            if (growth && max >= 2) return 2;
        }

        return max;
    }, [sortedData, sortOption]);

export const getSortOptionKey = ({ sortBy, ascending }) =>
    `${sortBy}-${ascending ? 'asc' : 'desc'}`;

export const useSortStateOnly = (initialSortOption) => {
    const [sortOption, setSortOption] = useState(initialSortOption);
    return {
        setSortOption,
        sortOption,
        sortOptionKey: getSortOptionKey(sortOption),
    };
};

const useSort = (data, handleSort, initialSortOption) => {
    const sortStateOnly = useSortStateOnly(initialSortOption);
    const { ascending, sortBy } = sortStateOnly.sortOption;
    const sortedData = useMemoizedSort(data, sortBy, ascending, handleSort);
    const maxSortMetric = useMaxSortMetric(
        sortedData,
        sortStateOnly.sortOption,
    );

    return {
        ...sortStateOnly,
        sortedData,
        maxSortMetric,
    };
};

const fallbackSortOptionKeyAccessor = ({ label, ascending }) =>
    `${label} ${ascending ? 'a' : 'de'}scending`;

export const useSortSearchParamsStateOnly = ({
    initialSortOption,
    options,
    sortOptionKeyAccessor,
    paramKey = 'sort',
}) => {
    const history = useHistory();
    const location = useLocation();
    const searchParams = useURLSearchParams();
    const sortOptionKey =
        searchParams.get(paramKey) || sortOptionKeyAccessor(initialSortOption);
    const sortOption =
        options.find((option) =>
            [
                sortOptionKeyAccessor(option),
                fallbackSortOptionKeyAccessor(option),
            ].includes(sortOptionKey),
        ) || initialSortOption;

    const setSortOption = useCallback(
        (option) => {
            const { pathname, hash } = location;
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set(paramKey, sortOptionKeyAccessor(option));
            history.replace(
                createPath({
                    pathname,
                    hash,
                    search: `?${searchParams.toString()}`,
                }),
            );
        },
        [sortOptionKeyAccessor, history, location],
    );

    return {
        setSortOption,
        sortOption,
        sortOptionKey: getSortOptionKey(sortOption),
    };
};

export const useSortSearchParams = ({
    data,
    handleSort,
    initialSortOption,
    options,
    sortOptionKeyAccessor,
    paramKey = 'sort',
}) => {
    const sortSearchParamsStateOnly = useSortSearchParamsStateOnly({
        initialSortOption,
        options,
        sortOptionKeyAccessor,
        paramKey,
    });
    const sortOption = sortSearchParamsStateOnly.sortOption;
    const { ascending, sortBy } = sortOption;
    const sortedData = useMemoizedSort(data, sortBy, ascending, handleSort);
    const maxSortMetric = useMaxSortMetric(sortedData, sortOption);
    return {
        ...sortSearchParamsStateOnly,
        sortedData,
        maxSortMetric,
    };
};

export default useSort;
